import { useEffect, useRef, useState } from "react";
import { Minus, Plus, RotateCcw } from "lucide-react";
import { useIdioma } from "../../i18n/IdiomaProvider";
import { ModalCadastro } from "./ModalCadastro";
import { BotaoContorno } from "./BotaoAcao";
import { BotaoTopoPrimario } from "./BotoesTopo";
import styles from "./ModalAjusteImagem.module.css";

interface ModalAjusteImagemProps {
  aberto: boolean;
  /** Imagem escolhida, em data URL. */
  imagem: string;
  titulo?: string;
  /** Lado do recorte final, em pixels (o resultado é sempre quadrado). */
  tamanhoFinal?: number;
  onCancelar: () => void;
  /** Recebe o recorte pronto, em PNG data URL. */
  onConfirmar: (dataUrl: string) => void;
}

/** Lado da área de recorte na tela (o círculo do avatar). */
const LADO_AREA = 280;
const ZOOM_MIN = 1;
const ZOOM_MAX = 4;

/**
 * Ajuste da imagem do avatar antes de enviar: a foto aparece dentro do círculo
 * do avatar e o usuário arrasta e aproxima até enquadrar. O recorte sai
 * quadrado (o círculo é só a máscara visual, como em todo avatar da
 * plataforma), gerado por canvas — sem biblioteca externa.
 */
export function ModalAjusteImagem({
  aberto,
  imagem,
  titulo = "Ajustar imagem",
  tamanhoFinal = 512,
  onCancelar,
  onConfirmar,
}: ModalAjusteImagemProps) {
  const { traduzir } = useIdioma();
  const imagemRef = useRef<HTMLImageElement | null>(null);
  const arrasteRef = useRef<{ x: number; y: number; posX: number; posY: number } | null>(null);
  const [dimensoes, setDimensoes] = useState({ largura: 0, altura: 0 });
  const [zoom, setZoom] = useState(1);
  const [posicao, setPosicao] = useState({ x: 0, y: 0 });
  const [processando, setProcessando] = useState(false);

  /* Carrega a imagem para saber o tamanho real e centralizar o enquadramento. */
  useEffect(() => {
    if (!aberto || !imagem) return;
    const elemento = new Image();
    elemento.onload = () => {
      imagemRef.current = elemento;
      setDimensoes({ largura: elemento.naturalWidth, altura: elemento.naturalHeight });
      setZoom(1);
      setPosicao({ x: 0, y: 0 });
    };
    elemento.src = imagem;
    return () => {
      elemento.onload = null;
    };
  }, [aberto, imagem]);

  /* Escala que faz a imagem cobrir o círculo por inteiro (nunca sobra vazio). */
  const escalaBase = dimensoes.largura && dimensoes.altura
    ? Math.max(LADO_AREA / dimensoes.largura, LADO_AREA / dimensoes.altura)
    : 1;
  const larguraExibida = dimensoes.largura * escalaBase * zoom;
  const alturaExibida = dimensoes.altura * escalaBase * zoom;
  const limiteX = Math.max(0, (larguraExibida - LADO_AREA) / 2);
  const limiteY = Math.max(0, (alturaExibida - LADO_AREA) / 2);

  function limitar(valor: number, limite: number) {
    return Math.min(limite, Math.max(-limite, valor));
  }

  function aplicarPosicao(x: number, y: number) {
    setPosicao({ x: limitar(x, limiteX), y: limitar(y, limiteY) });
  }

  function alterarZoom(novo: number) {
    const proximo = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number(novo.toFixed(2))));
    setZoom(proximo);
    /* Reaplica os limites: ao afastar, a imagem menor não pode ficar fora. */
    const novoLimiteX = Math.max(0, (dimensoes.largura * escalaBase * proximo - LADO_AREA) / 2);
    const novoLimiteY = Math.max(0, (dimensoes.altura * escalaBase * proximo - LADO_AREA) / 2);
    setPosicao((atual) => ({
      x: Math.min(novoLimiteX, Math.max(-novoLimiteX, atual.x)),
      y: Math.min(novoLimiteY, Math.max(-novoLimiteY, atual.y)),
    }));
  }

  function iniciarArraste(evento: React.PointerEvent<HTMLDivElement>) {
    evento.currentTarget.setPointerCapture(evento.pointerId);
    arrasteRef.current = { x: evento.clientX, y: evento.clientY, posX: posicao.x, posY: posicao.y };
  }

  function arrastar(evento: React.PointerEvent<HTMLDivElement>) {
    const inicio = arrasteRef.current;
    if (!inicio) return;
    aplicarPosicao(
      inicio.posX + (evento.clientX - inicio.x),
      inicio.posY + (evento.clientY - inicio.y)
    );
  }

  function encerrarArraste() {
    arrasteRef.current = null;
  }

  function aoRolar(evento: React.WheelEvent<HTMLDivElement>) {
    evento.preventDefault();
    alterarZoom(zoom + (evento.deltaY > 0 ? -0.12 : 0.12));
  }

  function reiniciar() {
    setZoom(1);
    setPosicao({ x: 0, y: 0 });
  }

  /* Recorta o que está dentro da área: mesma conta da exibição, só que na
     resolução final escolhida. */
  function confirmar() {
    const elemento = imagemRef.current;
    if (!elemento) return;
    setProcessando(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = tamanhoFinal;
      canvas.height = tamanhoFinal;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const proporcao = tamanhoFinal / LADO_AREA;
      const escala = escalaBase * zoom * proporcao;
      const largura = dimensoes.largura * escala;
      const altura = dimensoes.altura * escala;
      const x = (tamanhoFinal - largura) / 2 + posicao.x * proporcao;
      const y = (tamanhoFinal - altura) / 2 + posicao.y * proporcao;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, tamanhoFinal, tamanhoFinal);
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(elemento, x, y, largura, altura);
      onConfirmar(canvas.toDataURL("image/png"));
    } finally {
      setProcessando(false);
    }
  }

  return (
    <ModalCadastro
      aberto={aberto}
      titulo={titulo}
      subtitulo="Arraste a imagem e use o zoom para enquadrar dentro do círculo."
      largura={520}
      onFechar={onCancelar}
      rodape={
        <>
          <BotaoContorno estilo="topo" onClick={onCancelar} disabled={processando}>
            Cancelar
          </BotaoContorno>
          <BotaoTopoPrimario carregando={processando} onClick={confirmar}>
            Usar imagem
          </BotaoTopoPrimario>
        </>
      }
    >
      <div className={styles.conteudo}>
        <div
          className={styles.area}
          style={{ width: LADO_AREA, height: LADO_AREA }}
          onPointerDown={iniciarArraste}
          onPointerMove={arrastar}
          onPointerUp={encerrarArraste}
          onPointerCancel={encerrarArraste}
          onWheel={aoRolar}
          role="img"
          aria-label={traduzir("Pré-visualização do avatar")}
        >
          {imagem ? (
            <img
              className={styles.imagem}
              src={imagem}
              alt=""
              draggable={false}
              style={{
                width: larguraExibida || LADO_AREA,
                height: alturaExibida || LADO_AREA,
                transform: `translate(calc(-50% + ${posicao.x}px), calc(-50% + ${posicao.y}px))`,
              }}
            />
          ) : null}
          {/* Máscara: escurece o que fica fora do círculo do avatar. */}
          <span className={styles.mascara} aria-hidden="true" />
        </div>

        <div className={styles.controles}>
          <button
            type="button"
            className={styles.botaoZoom}
            aria-label={traduzir("Afastar")}
            onClick={() => alterarZoom(zoom - 0.2)}
          >
            <Minus aria-hidden="true" />
          </button>
          <input
            className={styles.slider}
            type="range"
            min={ZOOM_MIN}
            max={ZOOM_MAX}
            step={0.01}
            value={zoom}
            aria-label={traduzir("Zoom")}
            onChange={(evento) => alterarZoom(Number(evento.target.value))}
          />
          <button
            type="button"
            className={styles.botaoZoom}
            aria-label={traduzir("Aproximar")}
            onClick={() => alterarZoom(zoom + 0.2)}
          >
            <Plus aria-hidden="true" />
          </button>
          <button
            type="button"
            className={styles.botaoZoom}
            aria-label={traduzir("Recomeçar o enquadramento")}
            title={traduzir("Recomeçar o enquadramento")}
            onClick={reiniciar}
          >
            <RotateCcw aria-hidden="true" />
          </button>
        </div>
      </div>
    </ModalCadastro>
  );
}
